// ─────────────────────────────────────────────────────────────────────────
// SignatureModal.tsx
// Bottom-sheet modal wrapping useSignatureCanvas, ported from the
// "9. SIGNATURE CANVAS & MODAL" block + #signature-modal markup.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { Signature, X, Eraser, FloppyDisk } from '@phosphor-icons/react';
import { useLanguage } from './LanguageContext';
import { useSignatureCanvas } from './useSignatureCanvas';

export interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the signature PNG data URI once the user saves. */
  onSave: (signatureDataURI: string) => void;
}

export default function SignatureModal({ open, onClose, onSave }: SignatureModalProps) {
  const { t } = useLanguage();
  const sig = useSignatureCanvas();
  const [showEmptyError, setShowEmptyError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Mirrors openSignatureModal(): once visible, size the canvas to its container.
  useEffect(() => {
    if (open) sig.prepareForDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Lock body scroll behind the bottom-sheet while open, matching the
  // original's document.body.classList.add/remove('modal-open').
  useEffect(() => {
    if (!open) return;
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  if (!open) return null;

  const handleSave = () => {
    if (!sig.hasSignature) {
      setShowEmptyError(true);
      window.setTimeout(() => setShowEmptyError(false), 600);
      return;
    }
    onSave(sig.toDataURL());
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-brand-900/80 backdrop-blur-sm">
      <div className="mx-auto flex h-[78vh] w-full max-w-md flex-col rounded-t-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between rounded-t-3xl border-b border-gray-100 bg-brand-50 px-5 py-4">
          <div>
            <h3 className="text-xl font-bold text-brand-900">{t('signatureModalTitle')}</h3>
            <p className="text-xs text-gray-500">{t('signatureModalSubtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-900 transition active:bg-brand-200"
          >
            <X weight="bold" />
          </button>
        </div>

        <div className="flex flex-1 flex-col bg-white p-5">
          <div className="mb-3 text-center">
            <p className="flex items-center justify-center gap-2.5 text-base font-bold text-brand-900">
              {t('signHere')}
            </p>
          </div>
          <div
            ref={containerRef}
            className={[
              'relative flex-1 overflow-hidden rounded-2xl border-4 shadow-xl transition-colors',
              showEmptyError ? 'border-red-500 bg-red-50' : 'border-brand-900 bg-white',
            ].join(' ')}
          >
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.06]">
              <Signature className="text-[10rem] text-brand-900" />
            </div>
            <canvas
              id="signature-canvas"
              ref={sig.canvasRef}
              className="h-full w-full cursor-crosshair bg-white"
              style={{ touchAction: 'none' }}
              {...sig.canvasHandlers}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 border-t border-gray-100 bg-white p-5 pb-8">
          <button
            type="button"
            onClick={sig.clear}
            className="flex items-center justify-center gap-2 rounded-xl border-2 border-red-200 px-3 py-4 text-sm font-bold text-red-600 transition active:bg-red-50"
          >
            <Eraser weight="fill" className="text-lg" />
            <span>{t('clearSignature')}</span>
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex items-center justify-center gap-2 rounded-xl bg-brand-900 px-3 py-4 text-sm font-bold text-white shadow-xl transition-all active:scale-[0.98] active:bg-brand-800"
          >
            <FloppyDisk weight="fill" className="text-lg" />
            <span>{t('saveAndComplete')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
